import { BucketId, FileMetadata, MeResponse } from "../types/File";
import {
  constructPublicUrl,
  createMinimalSafeHeaders,
} from "../utils/urlUtils";

const jsonHeaders = () =>
  createMinimalSafeHeaders({
    Accept: "application/json",
    "Content-Type": "application/json",
  });

/**
 * Service for managing files through the Decentraland File Manager API.
 *
 * Requests go to the same-origin `/api` path, which is served by the Cloudflare Pages
 * Function (functions/api). Identity comes from the Cloudflare Access JWT that Access
 * injects on the gated custom domain, so the browser itself sends no credentials.
 */
class FileService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "/api";
  }

  /**
   * Returns the caller's identity and the buckets they may access.
   */
  async getMe(): Promise<MeResponse> {
    const response = await fetch(`${this.baseUrl}/me`, {
      method: "GET",
      headers: jsonHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Fetches ALL files for a bucket, following pagination, sorted by lastModified desc.
   */
  async getAllFiles(bucket: BucketId, prefix?: string): Promise<FileMetadata[]> {
    const allFiles: FileMetadata[] = [];
    let continuationToken: string | undefined;
    let hasMore = true;

    try {
      while (hasMore) {
        const params = new URLSearchParams();
        params.append("bucket", bucket);
        params.append("limit", "1000"); // Max S3 limit

        if (prefix) {
          params.append("prefix", prefix);
        }
        if (continuationToken) {
          params.append("continuationToken", continuationToken);
        }

        const response = await fetch(`${this.baseUrl}/files?${params.toString()}`, {
          method: "GET",
          headers: jsonHeaders(),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.files) {
          const transformedFiles = data.files.map((file: any) => ({
            ...file,
            url: constructPublicUrl(file.key, bucket),
          }));
          allFiles.push(...transformedFiles);
        }

        hasMore = data.pagination?.hasMore || false;
        continuationToken = data.pagination?.nextContinuationToken;
      }

      // Sort all files by lastModified (newest first)
      allFiles.sort((a, b) => {
        const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
        const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
        return dateB - dateA;
      });

      return allFiles;
    } catch (error) {
      throw normalizeError(error, "Failed to load files");
    }
  }

  /**
   * Deletes a file by its key from the given bucket.
   */
  async deleteFile(key: string, bucket: BucketId): Promise<void> {
    try {
      const response = await fetch(
        `${this.baseUrl}/files/${encodeURIComponent(key)}?bucket=${bucket}`,
        {
          method: "DELETE",
          headers: jsonHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      throw normalizeError(error, "Failed to delete file");
    }
  }

}

function normalizeError(error: unknown, context: string): Error {
  if (error instanceof Error) {
    if (error.message.includes("CORS") || error.message.includes("Failed to fetch")) {
      return new Error(
        "Network error: unable to reach the file management server. Please try again."
      );
    }
    return new Error(`${context}: ${error.message}`);
  }
  return new Error(`${context}: Unknown error`);
}

export const fileService = new FileService();
